import { Maybe, RegionId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { Feature } from "components/feature";
import { trpc } from "components/rpc/trpc";
import { CalendarDateInput } from "components/ui/calendar-date";
import { Checkbox } from "components/ui/checkbox";
import { fancyAlert } from "components/ui/fancy-alert";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import moment from "moment";
import * as React from "react";
import { useState } from "react";
import { Radio, RadioOption } from "../shared/radio";

interface CSVDownloadProps {
    onDismiss: () => void;
    regionId: RegionId;
    regionName: string;
    canAccessMultipleRegions: boolean;
}

const clientDownloadOptions: RadioOption[] = [
    {
        key: "all",
        text: "Download all clients regardless of date values",
    },
    {
        key: "creationDate",
        text: "Only download clients that were added within a certain date range",
    },
    {
        key: "placementDate",
        text: "Only download clients that have Move dates in a certain range",
    },
];

type ClientDownloadOption = "all" | "creationDate" | "placementDate";

export const CSVDownload: React.FC<CSVDownloadProps> = props => {
    const [downloadClients, setDownloadClients] = useState(false);
    const [downloadCommunities, setDownloadCommunities] = useState(false);
    const [downloadCommunityContacts, setDownloadCommunityContacts] = useState(false);
    const [downloadReferralSources, setDownloadReferralSources] = useState(false);
    const [downloadInvoices, setDownloadInvoices] = useState(false);
    const [downloadDisclosureAcknowledgements, setDownloadDisclosureAcknowledgements] =
        useState(false);
    const [downloadClientStartDate, setDownloadClientStartDate] = useState<Date | null>(
        null,
    );
    const [downloadClientEndDate, setDownloadClientEndDate] = useState<Date | null>(null);
    const [clientsToDownload, setClientsToDownload] =
        useState<ClientDownloadOption>("all");
    const [showAdvancedClientDownloadOptions, setShowAdvancedClientDownloadOptions] =
        useState(false);
    const [
        showAdvancedCommunityDownloadOptions,
        setShowAdvancedCommunityDownloadOptions,
    ] = useState(false);
    const [myClientsOnly, setMyClientsOnly] = useState(false);
    const [includeClientCustomQuestions, setIncludeClientCustomQuestions] =
        useState(false);
    const [includeCommunityCustomQuestions, setIncludeCommunityCustomQuestions] =
        useState(false);
    const [showSuccessBanner, setShowSuccessBanner] = useState(false);

    const trpcClient = trpc.useTRPC();
    const createMutation = useMutation({
        ...trpcClient.dataExport.create.mutationOptions(),
        onSuccess: () => {
            setShowSuccessBanner(true);
        },
        onError: async () => {
            await fancyAlert(
                "Export Error",
                "An error occurred while processing your export.",
                "warning",
            );
        },
    });

    const download = () => {
        const dateFieldName =
            clientsToDownload === "creationDate" ? "clientStartDate" : "startMoveDate";
        const endDateFieldName =
            clientsToDownload === "creationDate" ? "clientEndDate" : "endMoveDate";

        const body = {
            downloadClients,
            downloadCommunities,
            downloadCommunityContacts,
            downloadReferralSources,
            downloadInvoices,
            downloadDisclosureAcknowledgements,
            myClientsOnly,
            includeClientCustomQuestions,
            includeCommunityCustomQuestions,
            [dateFieldName]: Maybe.fromValue(downloadClientStartDate)
                .map(d => d.toISOString())
                .getOrElse(undefined),
            [endDateFieldName]: Maybe.fromValue(downloadClientEndDate)
                .map(d => d.toISOString())
                .getOrElse(undefined),
        };

        createMutation.mutate(body);
    };

    const downloadCheckboxSetting = (
        value: boolean,
        onChange: (checked: boolean) => void,
        checkboxLabel: string,
        checkboxDescription?: string,
        advancedCheckboxOptions?: () => JSX.Element | null,
    ): JSX.Element => {
        return (
            <div>
                <div>
                    <Checkbox
                        checked={value}
                        onChange={newValue => onChange(newValue.target.checked)}
                        label={checkboxLabel}
                    />
                    <div>{checkboxDescription}</div>
                </div>
                <div className="advanced-checkbox-options">
                    {advancedCheckboxOptions && advancedCheckboxOptions()}
                </div>
            </div>
        );
    };

    const regionSelection = (): JSX.Element | null => {
        if (!props.canAccessMultipleRegions) {
            return null;
        }
        const options: RadioOption[] = [
            {
                key: props.regionId,
                text: `This office only (${props.regionName})`,
            },
            {
                key: "all",
                text: "All offices I have access to",
            },
        ];
        return (
            <Radio
                options={options}
                value={props.regionId}
                label="Download CSV for"
                onChange={() => {}}
            />
        );
    };

    const clientDateRestrictionChoices = (): JSX.Element => {
        return (
            <Radio
                options={clientDownloadOptions}
                value={clientsToDownload}
                label="Date Filters"
                onChange={newVal => setClientsToDownload(newVal as ClientDownloadOption)}
            />
        );
    };

    const showAdvancedClientOptions = (): JSX.Element | null => {
        if (!downloadClients || showAdvancedClientDownloadOptions) {
            return null;
        }

        return (
            <a
                href="#"
                className={`text-sm ${defaultLinkStyle}`}
                onClick={() => setShowAdvancedClientDownloadOptions(true)}>
                Show Advanced Options
            </a>
        );
    };

    const showAdvancedCommunityOptions = (): JSX.Element | null => {
        if (!downloadCommunities || showAdvancedCommunityDownloadOptions) {
            return null;
        }

        return (
            <a
                href="#"
                className={`text-sm ${defaultLinkStyle}`}
                onClick={() => setShowAdvancedCommunityDownloadOptions(true)}>
                Show Advanced Options
            </a>
        );
    };

    const advancedClientOptions = (): JSX.Element | null => {
        if (!downloadClients || !showAdvancedClientDownloadOptions) return null;

        const dateSelection = (
            <div className="options">
                <div className="option">
                    <CalendarDateInput
                        label="Start date"
                        onChange={cd => {
                            setDownloadClientStartDate(
                                cd ? moment(cd.toString()).utc().toDate() : null,
                            );
                        }}
                    />
                </div>
                <div className="option">
                    <CalendarDateInput
                        label="End date"
                        onChange={cd => {
                            setDownloadClientEndDate(
                                cd ? moment(cd.toString()).utc().toDate() : null,
                            );
                        }}
                    />
                </div>
            </div>
        );

        return (
            <div className="csv--client-download-options">
                {downloadCheckboxSetting(
                    myClientsOnly,
                    setMyClientsOnly,
                    "Only Download Clients Assigned to me (not all clients)",
                )}
                {downloadCheckboxSetting(
                    includeClientCustomQuestions,
                    setIncludeClientCustomQuestions,
                    "Include Custom Question Fields/Answers",
                )}
                {clientDateRestrictionChoices()}
                {clientsToDownload != "all" && dateSelection}
            </div>
        );
    };

    const advancedCommunityOptions = (): JSX.Element | null => {
        if (!downloadCommunities || !showAdvancedCommunityDownloadOptions) return null;

        return (
            <div className="csv--community-download-options">
                {downloadCheckboxSetting(
                    includeCommunityCustomQuestions,
                    setIncludeCommunityCustomQuestions,
                    "Include Custom Question Fields/Answers",
                )}
            </div>
        );
    };

    const downloadClientAdvancedOptions = (): JSX.Element | null => {
        return (
            <div>
                {showAdvancedClientOptions()}
                {advancedClientOptions()}
            </div>
        );
    };

    const downloadCommunityAdvancedOptions = (): JSX.Element | null => {
        return (
            <div>
                {showAdvancedCommunityOptions()}
                {advancedCommunityOptions()}
            </div>
        );
    };

    const isAnyCheckboxChecked = (): boolean => {
        return (
            downloadClients ||
            downloadCommunities ||
            downloadCommunityContacts ||
            downloadReferralSources ||
            downloadInvoices ||
            downloadDisclosureAcknowledgements
        );
    };

    return (
        <Panel
            type={PanelType.large}
            hasCloseButton={true}
            onDismiss={() => props.onDismiss()}
            isOpen={true}
            headerText="Export CSV Data">
            <div className="space-y-4">
                {showSuccessBanner && (
                    <InlineBanner type="success">
                        Your export is being processed. You will receive an email with a
                        download link when it is ready.
                    </InlineBanner>
                )}
                <h3>Select at least one set of data to export:</h3>
                {downloadCheckboxSetting(
                    downloadClients,
                    setDownloadClients,
                    "Clients",
                    "Download basic client data for active and closed clients.",
                    downloadClientAdvancedOptions,
                )}
                {downloadCheckboxSetting(
                    downloadCommunities,
                    setDownloadCommunities,
                    "Communities",
                    "Download basic community data for all communities.",
                    downloadCommunityAdvancedOptions,
                )}
                {downloadCheckboxSetting(
                    downloadCommunityContacts,
                    setDownloadCommunityContacts,
                    "Community Contacts",
                    "Download all contacts for all communities.",
                )}
                {downloadCheckboxSetting(
                    downloadReferralSources,
                    setDownloadReferralSources,
                    "Referral Sources",
                    "Download basic referral source details.",
                )}
                {downloadCheckboxSetting(
                    downloadInvoices,
                    setDownloadInvoices,
                    "Invoices",
                    "Download all invoice data.",
                )}
                <Feature name="clientDisclosures">
                    {downloadCheckboxSetting(
                        downloadDisclosureAcknowledgements,
                        setDownloadDisclosureAcknowledgements,
                        "Disclosure Acknowledgements",
                        "Download all disclosure acknowledgements.",
                    )}
                </Feature>
                {regionSelection()}
                <div className="flex items-center space-x-4 justify-end">
                    <SecondaryButton type="button" onClick={() => props.onDismiss()}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        type="button"
                        disabled={!isAnyCheckboxChecked() || createMutation.isPending}
                        onClick={() => download()}>
                        {createMutation.isPending ? (
                            <>
                                <Spinner />
                                <span className="ml-2">Preparing...</span>
                            </>
                        ) : (
                            "Export"
                        )}
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};
